import ShopDTO from "@/dto/shop/ShopDTO";
import React, { useEffect } from "react";
import OrderTab from "../../../enum/OrderTab";
import { useGlobalStore } from "@/store/hooks";
import { useOrderManagementStore } from "../../../store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import { Button, Checkbox, Popover } from "antd";
import "./index.less";
import { ShippingDeadlineShopTypes } from "@/utils/ShopUtils";
import { delay } from "lodash";
import QuickEnum from "@/scenes/OrderManagement/enum/QuickEnum";
import { InfoCircleFilled } from "@ant-design/icons";
import { useHistory } from "react-router";
import { showDeadlineShippingOrderTabs } from "@/utils/OrderTabUtil";
import { OrderQuickSearchType } from "../SearchPane/QuickField";
import ShopType from "@/enum/ShopType";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

interface ShippingDeadlineCountProps {
    shop: ShopDTO;
    tab: OrderTab;
}

const ShippingDeadlineCount: React.FC<ShippingDeadlineCountProps> = ({ shop, tab }) => {
    const { shopMapping, shopSettingMapping } = useGlobalStore();
    const history = useHistory();
    const shopType = shopMapping.get(shop?.id)?.type;
    const shopSetting = shopSettingMapping.get(shop?.id);

    const orderManagementStore = useOrderManagementStore();

    const noShippingDeadlineCount =
        !orderManagementStore.orderListCount?.shippingDeadlinePendingShipmentOrderCount &&
        !orderManagementStore.orderListCount?.shippingDeadlineOnHoldOrderCount &&
        !orderManagementStore.orderListCount?.shippingDeadlineRefundingOrderCount;

    const hideShippingDeadlineCount =
        !showDeadlineShippingOrderTabs.includes(tab) ||
        !ShippingDeadlineShopTypes.includes(shopType) ||
        noShippingDeadlineCount;

    const isPDD = [ShopType.PINDUODUO, ShopType.PDD_FDS].includes(shopType);

    useEffect(() => {
        return () => {
            orderManagementStore.table.switchShippingDeadlineChecked(false);
        };
    }, [tab]);

    if (hideShippingDeadlineCount) return null;

    function handleChange(e) {
        const checked = e.target.checked;

        orderManagementStore.table.switchShippingDeadlineChecked(checked);

        let { quicks } = orderManagementStore.table.form.getFieldsValue();

        if (checked) {
            quicks = quicks?.includes(QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE)
                ? [QuickEnum.QUICK_DELIVERY_TIMEOUT, QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE]
                : [QuickEnum.QUICK_DELIVERY_TIMEOUT];
        } else {
            quicks = quicks?.includes(QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE)
                ? [QuickEnum.QUICK_SHIPPING_DEADLINE_OVERDUE]
                : [];
        }

        orderManagementStore.table.resetFormValues();

        delay(() => {
            orderManagementStore.table.form.setFieldsValue({
                quicks,
                orderQuickSearchType: OrderQuickSearchType.AnyMatchQuickCriterion,
            });
            orderManagementStore.table.changePageNo(1);
            orderManagementStore.fetchOrders(shop.id, tab);
        }, 10);

        collectEvent(DataTrackEvent.QUICK_SEARCH_ORDER, {
            shop_id: shop.id,
            shop_name: shop.name,
            shop_type: ShopTypeNameMap[shop.type],
            type: "筛选即将发货的订单",
            time: shopSetting?.shippingDeadlineWarnHours,
        });
    }

    return (
        <Popover
            title={
                <div>
                    <div>
                        <InfoCircleFilled style={{ color: "rgb(17,145,255)" }} />
                        <span className="fb ml10">
                            点击可快速筛选{shopSetting?.shippingDeadlineWarnHours} 小时内
                            {isPDD ? "需发货或揽收" : "需发货"}的订单
                        </span>
                    </div>
                    {tab !== OrderTab.PENDING_SHIPMENT &&
                    orderManagementStore.orderListCount?.shippingDeadlinePendingShipmentOrderCount ? (
                        <div className="error fb ml25">
                            {shopSetting?.shippingDeadlineWarnHours}小时内
                            {isPDD ? "需要发货或揽收" : "需要发货"}的待发货订单需到‘待发货’状态列表下处理
                        </div>
                    ) : null}
                    {tab !== OrderTab.ON_HOLD &&
                    orderManagementStore.orderListCount?.shippingDeadlineOnHoldOrderCount ? (
                        <div className="error fb ml25">
                            {shopSetting?.shippingDeadlineWarnHours}小时内
                            {isPDD ? "需要发货或揽收" : "需要发货"}的挂起订单需到‘挂起’状态列表下处理
                        </div>
                    ) : null}
                    {tab !== OrderTab.PENDING_SHIPMENT_REFUNDING &&
                    orderManagementStore.orderListCount?.shippingDeadlineRefundingOrderCount ? (
                        <div className="error fb ml25">
                            {shopSetting?.shippingDeadlineWarnHours}小时内
                            {isPDD ? "需要发货或揽收" : "需要发货"}的退款中订单需到‘退款中’状态列表下处理
                        </div>
                    ) : null}
                </div>
            }
            content={
                <div style={{ textAlign: "right" }}>
                    <Button
                        size="small"
                        type="primary"
                        onClick={() => {
                            history.push("/setting/shop");
                        }}
                    >
                        超时提醒设置
                    </Button>
                </div>
            }
        >
            <span className="tc mb5 inline-block shipping-deadline-count-container">
                <Checkbox
                    style={{ backgroundColor: "rgb(193, 0, 16)" }}
                    className="text-white"
                    onChange={handleChange}
                    checked={orderManagementStore.table.shippingDeadlineChecked}
                >
                    <span>
                        {shopSetting?.shippingDeadlineWarnHours}小时内{isPDD ? "需发货或揽收" : "需发货"}:
                    </span>

                    {[
                        {
                            label: "待发货",
                            count: orderManagementStore.orderListCount?.shippingDeadlinePendingShipmentOrderCount,
                            className: "ml10",
                        },
                        {
                            label: "挂起",
                            count: orderManagementStore.orderListCount?.shippingDeadlineOnHoldOrderCount,
                        },
                        {
                            label: "退款中",
                            count: orderManagementStore.orderListCount?.shippingDeadlineRefundingOrderCount,
                        },
                    ]
                        .filter((item) => item.count)
                        .map((item, index, array) => (
                            <span key={item.label} className={index === 0 ? "ml10" : ""}>
                                {item.label}
                                {item.count}
                                {index < array.length - 1 && "，"}
                            </span>
                        ))}
                </Checkbox>
            </span>
        </Popover>
    );
};

export default observer(ShippingDeadlineCount);
